import { isDetachedWindow } from '../utils/detachedMode';
import { useLayoutStore } from './layoutStore';
import { useChatTabsStore } from './chatTabsStore';
import { useChatInstancesStore } from './chatInstancesStore';
import { useProjectStore } from './projectStore';
import type {
  StoreFullSyncPayload,
  StoreSyncPayload,
} from '../electron/detached-window-types';
import type { ChatInstancesSyncSlice } from './crossWindowSync.types';
import {
  diffSyncSlice,
  parseChatInstancesSyncPatch,
  pickChatTabsSyncSlice,
  pickLayoutSyncSlice,
  pickProjectSyncSlice,
  restoreQueuedMessagesFromSync,
  sanitizeChatTabsSyncPatch,
  sanitizeLayoutSyncPatch,
  sanitizeProjectSyncPatch,
  serializeQueuedMessagesForSync,
} from './crossWindowSync.utils';

let isApplyingRemoteSync = false;
let windowInstanceId = '';
let isSetup = false;
let previousChatInstancesSyncSlice: ChatInstancesSyncSlice =
  serializeQueuedMessagesForSync(new Map());
let hasReceivedInitialFullSync = !isDetachedWindow();
let fullSyncRetryTimeoutId: ReturnType<typeof setTimeout> | undefined;
const syncReadyListeners = new Set<() => void>();

const DETACHED_FULL_SYNC_RETRY_MS = 3_000;

export function isCrossWindowSyncReady(): boolean {
  return hasReceivedInitialFullSync;
}

export function subscribeCrossWindowSyncReady(
  listener: () => void,
): () => void {
  syncReadyListeners.add(listener);

  return () => {
    syncReadyListeners.delete(listener);
  };
}

function clearFullSyncRetryTimeout(): void {
  if (fullSyncRetryTimeoutId === undefined) {
    return;
  }

  clearTimeout(fullSyncRetryTimeoutId);
  fullSyncRetryTimeoutId = undefined;
}

function markInitialFullSyncReceived(): void {
  if (hasReceivedInitialFullSync) {
    return;
  }

  clearFullSyncRetryTimeout();
  hasReceivedInitialFullSync = true;

  for (const listener of syncReadyListeners) {
    listener();
  }
}

function scheduleDetachedFullSyncRetry(): void {
  clearFullSyncRetryTimeout();

  if (hasReceivedInitialFullSync || !window.electronAPI?.detachedWindows) {
    return;
  }

  fullSyncRetryTimeoutId = setTimeout(() => {
    fullSyncRetryTimeoutId = undefined;

    if (hasReceivedInitialFullSync || !window.electronAPI?.detachedWindows) {
      return;
    }

    void window.electronAPI.detachedWindows.requestFullSync();
    scheduleDetachedFullSyncRetry();
  }, DETACHED_FULL_SYNC_RETRY_MS);
}

function applyChatInstancesPatch(patch: Record<string, unknown>): void {
  const parsed = parseChatInstancesSyncPatch(patch);

  if (!parsed?.queuedMessages) {
    return;
  }

  useChatInstancesStore.setState({
    queuedMessages: restoreQueuedMessagesFromSync(parsed.queuedMessages),
  });
}

function buildFullSyncPayload(): StoreFullSyncPayload {
  const layoutState = useLayoutStore.getState();
  const chatTabsState = useChatTabsStore.getState();
  const projectState = useProjectStore.getState();
  const { queuedMessages } = useChatInstancesStore.getState();

  return {
    layout: pickLayoutSyncSlice(layoutState),
    chatTabs: pickChatTabsSyncSlice(chatTabsState),
    chatInstances: serializeQueuedMessagesForSync(queuedMessages),
    project: pickProjectSyncSlice(projectState),
    sourceWindowId: windowInstanceId,
  };
}

function applyFullSync(payload: StoreFullSyncPayload): void {
  if (payload.sourceWindowId === windowInstanceId) {
    return;
  }

  const layoutPatch = sanitizeLayoutSyncPatch(payload.layout);
  const chatTabsPatch = sanitizeChatTabsSyncPatch(payload.chatTabs);
  const projectPatch = sanitizeProjectSyncPatch(payload.project);

  isApplyingRemoteSync = true;

  try {
    if (layoutPatch) {
      useLayoutStore.setState(layoutPatch);
    }

    if (chatTabsPatch) {
      useChatTabsStore.setState(chatTabsPatch);
    }

    if (projectPatch) {
      useProjectStore.setState(projectPatch);
    }

    applyChatInstancesPatch(payload.chatInstances);
    previousChatInstancesSyncSlice = serializeQueuedMessagesForSync(
      useChatInstancesStore.getState().queuedMessages,
    );
  } finally {
    isApplyingRemoteSync = false;
  }

  markInitialFullSyncReceived();
}

function applyStoreSync(payload: StoreSyncPayload): void {
  if (payload.sourceWindowId === windowInstanceId) {
    return;
  }

  isApplyingRemoteSync = true;

  try {
    if (payload.storeName === 'layout') {
      const layoutPatch = sanitizeLayoutSyncPatch(payload.patch);

      if (layoutPatch) {
        useLayoutStore.setState(layoutPatch);
      }

      return;
    }

    if (payload.storeName === 'chatTabs') {
      const chatTabsPatch = sanitizeChatTabsSyncPatch(payload.patch);

      if (chatTabsPatch) {
        useChatTabsStore.setState(chatTabsPatch);
      }

      return;
    }

    if (payload.storeName === 'project') {
      const projectPatch = sanitizeProjectSyncPatch(payload.patch);

      if (projectPatch) {
        useProjectStore.setState(projectPatch);
      }

      return;
    }

    if (payload.storeName === 'chatInstances') {
      applyChatInstancesPatch(payload.patch);
      previousChatInstancesSyncSlice = serializeQueuedMessagesForSync(
        useChatInstancesStore.getState().queuedMessages,
      );
    }
  } finally {
    isApplyingRemoteSync = false;
  }
}

function publishStoreSync(
  storeName: StoreSyncPayload['storeName'],
  patch: Record<string, unknown>,
): void {
  if (isApplyingRemoteSync || !patch || Object.keys(patch).length === 0) {
    return;
  }

  if (!window.electronAPI?.detachedWindows) {
    return;
  }

  void window.electronAPI.detachedWindows.syncStore({
    storeName,
    patch,
    sourceWindowId: windowInstanceId,
  });
}

export function setupCrossWindowSync(): () => void {
  if (isSetup || typeof window === 'undefined') {
    return () => undefined;
  }

  if (!window.electronAPI?.detachedWindows) {
    return () => undefined;
  }

  isSetup = true;
  windowInstanceId = crypto.randomUUID();
  previousChatInstancesSyncSlice = serializeQueuedMessagesForSync(
    useChatInstancesStore.getState().queuedMessages,
  );

  const isMainWindow = !isDetachedWindow();

  hasReceivedInitialFullSync = isMainWindow;

  const unsubLayout = useLayoutStore.subscribe((state, prevState) => {
    const patch = diffSyncSlice({
      current: pickLayoutSyncSlice(state),
      previous: pickLayoutSyncSlice(prevState),
    });

    if (patch) {
      publishStoreSync('layout', patch);
    }
  });

  const unsubChatTabs = useChatTabsStore.subscribe((state, prevState) => {
    const patch = diffSyncSlice({
      current: pickChatTabsSyncSlice(state),
      previous: pickChatTabsSyncSlice(prevState),
    });

    if (patch) {
      publishStoreSync('chatTabs', patch);
    }
  });

  const unsubProject = useProjectStore.subscribe((state, prevState) => {
    const patch = diffSyncSlice({
      current: pickProjectSyncSlice(state),
      previous: pickProjectSyncSlice(prevState),
    });

    if (patch) {
      publishStoreSync('project', patch);
    }
  });

  const unsubChatInstances = useChatInstancesStore.subscribe((state) => {
    const current = serializeQueuedMessagesForSync(state.queuedMessages);
    const patch = diffSyncSlice({
      current,
      previous: previousChatInstancesSyncSlice,
    });

    previousChatInstancesSyncSlice = current;
    if (patch) {
      publishStoreSync('chatInstances', patch);
    }
  });

  const cleanupStoreSync =
    window.electronAPI.detachedWindows.onStoreSync(applyStoreSync);

  const cleanupFullSync =
    window.electronAPI.detachedWindows.onFullSync(applyFullSync);

  const cleanupProvideFullSync = isMainWindow
    ? window.electronAPI.detachedWindows.onProvideFullSync(() => {
        void window.electronAPI.detachedWindows.respondFullSync(
          buildFullSyncPayload(),
        );
      })
    : () => undefined;

  if (!isMainWindow) {
    void window.electronAPI.detachedWindows.requestFullSync();
    scheduleDetachedFullSyncRetry();
  }

  return () => {
    clearFullSyncRetryTimeout();
    unsubLayout();
    unsubChatTabs();
    unsubProject();
    unsubChatInstances();
    cleanupStoreSync();
    cleanupFullSync();
    cleanupProvideFullSync();
    isSetup = false;
    hasReceivedInitialFullSync = !isDetachedWindow();
    syncReadyListeners.clear();
  };
}
